import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { type EmployeeWorkload, employeeWorkloadQueryOptions } from '@/entities/employee'
import { flattenPages } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

// Реестр «Сотрудники» — кто сколько тянет: строка на штатника с двумя числами,
// сами проекты открываются его страницей. Employee-only: заказчику здесь нечего
// делать.
export const Route = createFileRoute('/_app/employees/')({
  beforeLoad: ({ context }) => {
    if (context.session?.kind === 'customer') throw redirect({ to: '/projects' })
  },
  head: () => ({ meta: [{ title: 'Сотрудники · STIT Deal' }] }),
  component: EmployeesPage,
})

function EmployeesPage() {
  const q = useInfiniteQuery(employeeWorkloadQueryOptions)
  const rows = flattenPages(q.data?.pages)

  return (
    <div className="p-4 md:p-6">
      <PageHeader title="Сотрудники" />

      {q.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : q.error ? (
        <StatusScreen title="Не удалось загрузить сотрудников" message="Обновите страницу." />
      ) : rows.length === 0 ? (
        <EmptyState title="Сотрудников нет" description="Штат приходит из хаба." />
      ) : (
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <EmployeeRow key={row.id} row={row} />
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void q.fetchNextPage()}
                disabled={q.isFetchingNextPage}
              >
                {q.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Строка реестра целиком кликабельна — цель одна (страница сотрудника), поэтому
// ссылок внутри неё нет. На узких экранах числа уезжают под имя.
function EmployeeRow({ row }: { row: EmployeeWorkload }) {
  return (
    <li>
      <Link
        to="/employees/$employeeId"
        params={{ employeeId: row.id }}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-card bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset transition-colors hover:bg-bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
      >
        <Avatar name={row.full_name} size="sm" />
        <div className="min-w-0 flex-1 basis-48">
          <div className="truncate font-medium text-sm text-text-primary">{row.full_name}</div>
          {row.email && <div className="truncate text-text-tertiary text-xs">{row.email}</div>}
        </div>
        <div className="flex items-center gap-4">
          <RoleCount label="Ответственный" value={row.responsible_count} />
          <RoleCount label="В команде" value={row.team_count} />
        </div>
        <Icon name="chevron-right" className="shrink-0 text-text-tertiary" />
      </Link>
    </li>
  )
}

function RoleCount({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="label">{label}</span>
      <span className="mono-num text-sm text-text-primary">{value}</span>
    </div>
  )
}
